import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../Navbar";
import Footer from "../Footer";
import OutletSearch from "../SelectSearch/OutletSearch";
import { formatSalesValue } from "../../utils/formatUtils";
import ProductionHistoryTable from "./ProductionHistoryTable";
import * as XLSX from "xlsx";
import PageStatus from "../PageStatus/PageStatus";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const inputClass =
    "w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600";

const CentralKitchenProductionHistory = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const today = new Date().toLocaleDateString('en-CA');
    const token = localStorage.getItem("token");
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [filters, setFilters] = useState({
        from_date: today,
        to_date: today
    });

    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [productions, setProductions] = useState([]);

    const [selectedProduction, setSelectedProduction] = useState(null);
    const [showItemEntries, setShowItemEntries] = useState(false);

    const fetchProductions = async () => {
        if (!selectedOutlet) {
            toast.error("Please select outlet");
            return;
        }

        try {
            setLoading(true);

            const res = await axios.get(
                `${baseUrl}/getcentralkitchenproduction`,
                {
                    params: {
                        outlet: selectedOutlet,
                        from_date: filters.from_date,
                        to_date: filters.to_date
                    }
                }
            );

            setProductions(res.data.productions || []);
        } catch (err) {
            console.error(err);

            toast.error(
                err?.response?.data?.error || err?.response?.data?.message ||
                "Failed to fetch productions"
            );

            setError(
                err?.response?.data?.error || err?.response?.data?.message ||
                "Failed to fetch productions"
            )
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOutlet) {
            fetchProductions();
        }
    }, [selectedOutlet]);

    const totals = useMemo(() => {
        return productions.reduce(
            (acc, item) => {
                acc.totalAmount +=
                    parseFloat(item.total) || 0;

                acc.totalQty +=
                    parseFloat(item.total_quantity) || 0;

                acc.totalEntries += 1;

                return acc;
            },
            {
                totalAmount: 0,
                totalQty: 0,
                totalEntries: 0
            }
        );
    }, [productions]);

    const flatItems = useMemo(() => {
        return productions.flatMap((production) =>
            (production.items || []).map((item, index) => ({
                ...item,
                production_date: production.production_date,
                time: production.time,
                outlet: production.outlet,
                production_total: production.total,
                parent_id: production.id,
                index
            }))
        );
    }, [productions]);

    const itemSummary = flatItems.reduce((acc, item) => {
        const key = `${item.itemname}-${item.uom}-${item.rate}`;

        if (!acc[key]) {
            acc[key] = {
                itemname: item.itemname,
                uom: item.uom,
                quantity: 0,
                rate: parseFloat(item.rate || 0),
                total: 0,
            };
        }

        acc[key].quantity += parseFloat(item.quantity || 0);
        acc[key].total += parseFloat(item.total || 0);

        return acc;
    }, {});

    const summaryRows = Object.values(itemSummary);

    const exportToExcel = () => {
        let data = [];

        if (!showItemEntries) {
            data = productions.map((production, index) => ({
                SN: index + 1,

                "Production Date": new Date(
                    production.production_date
                ).toLocaleDateString(),

                Time: production.time || "-",

                "Item Count": production.item_count,

                "Total Quantity": parseFloat(
                    production.total_quantity || 0
                ),

                "Total Amount": parseFloat(
                    production.total || 0
                )
            }));

            data.push({});

            // Grand Total Row
            data.push({
                SN: "",
                "Production Date": "",
                Time: "",
                "Item Count": "Grand Total",
                "Total Quantity": productions.reduce(
                    (sum, item) =>
                        sum +
                        parseFloat(item.total_quantity || 0),
                    0
                ),
                "Total Amount": productions.reduce(
                    (sum, item) =>
                        sum +
                        parseFloat(item.total || 0),
                    0
                )
            });

        } else {
            data = flatItems.map((item, index) => ({
                SN: index + 1,

                "Production Date": new Date(
                    item.production_date
                ).toLocaleDateString(),

                Time: item.time || "-",

                Item: item.itemname,

                Quantity: parseFloat(item.quantity || 0),

                UOM: item.uom,

                Rate: parseFloat(item.rate || 0),

                Total: parseFloat(item.total || 0)
            }));

            data.push({});

            // Grand Total Row
            data.push({
                SN: "",
                "Production Date": "",
                Time: "",
                Item: "Grand Total",
                Quantity: flatItems.reduce(
                    (sum, item) =>
                        sum +
                        parseFloat(item.quantity || 0),
                    0
                ),
                UOM: "",
                Rate: "",
                Total: flatItems.reduce(
                    (sum, item) =>
                        sum +
                        parseFloat(item.total || 0),
                    0
                )
            });

            data.push({});
            data.push({});

            // Summary Heading
            data.push({
                Item: "Item Summary"
            });

            // Summary Table
            summaryRows.forEach((item) => {
                data.push({
                    Item: item.itemname,
                    Quantity: parseFloat(
                        item.quantity || 0
                    ),
                    UOM: item.uom,
                    Rate: parseFloat(
                        item.rate || 0
                    ),
                    Total: parseFloat(
                        item.total || 0
                    )
                });
            });

            data.push({});

            // Summary Grand Total
            data.push({
                Item: "Summary Total",
                Quantity: summaryRows.reduce(
                    (sum, item) =>
                        sum +
                        parseFloat(item.quantity || 0),
                    0
                ),
                UOM: "",
                Rate: "",
                Total: summaryRows.reduce(
                    (sum, item) =>
                        sum +
                        parseFloat(item.total || 0),
                    0
                )
            });
        }

        const worksheet = XLSX.utils.json_to_sheet([]);

        XLSX.utils.sheet_add_aoa(
            worksheet,
            [
                ["Central Kitchen Production History"],
                [`Outlet: ${selectedOutlet || "-"}`],
                [
                    `From Date: ${filters.from_date}    To Date: ${filters.to_date}`
                ],
                []
            ],
            { origin: "A1" }
        );

        XLSX.utils.sheet_add_json(
            worksheet,
            data,
            {
                origin: "A5",
                skipHeader: false
            }
        );

        worksheet["!cols"] = showItemEntries
            ? [
                { wch: 8 },
                { wch: 18 },
                { wch: 12 },
                { wch: 30 },
                { wch: 15 },
                { wch: 10 },
                { wch: 15 },
                { wch: 15 }
            ]
            : [
                { wch: 8 },
                { wch: 18 },
                { wch: 12 },
                { wch: 15 },
                { wch: 18 },
                { wch: 18 }
            ];

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Production History"
        );

        XLSX.writeFile(
            workbook,
            `Production_History_${Date.now()}.xlsx`
        );
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            <div className="flex-1 p-3 mb-4">
                <div className="max-w-5xl mx-auto">
                    <div className="bg-white border border-gray-100 rounded-md shadow-sm p-4 mb-4">
                        <div className="flex justify-between items-center mb-2.5">
                            <div className="flex-1 flex justify-start items-center gap-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    From Date
                                </label>

                                <input
                                    type="date"
                                    value={filters.from_date}
                                    onChange={(e) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            from_date:
                                                e.target.value
                                        }))
                                    }
                                    className={inputClass}
                                />
                            </div>

                            <div className="flex-1 flex justify-center">
                                <OutletSearch
                                    selectedOutlet={selectedOutlet}
                                    setSelectedOutlet={setSelectedOutlet}
                                    token={token}
                                />
                            </div>

                            <div className="flex-1 flex justify-end items-center gap-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    To Date
                                </label>

                                <input
                                    type="date"
                                    value={filters.to_date}
                                    onChange={(e) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            to_date:
                                                e.target.value
                                        }))
                                    }
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="flex justify-between items-end">
                            <div className="flex-1 flex justify-start gap-2">
                                <button
                                    onClick={() => navigate("/central-kitchen/production")}
                                    className="px-3 py-1.5 rounded-md font-medium bg-sky-100 text-sky-800 hover:bg-sky-200 hover:shadow-md text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Post Product
                                </button>

                                <button
                                    onClick={() => navigate("/central-kitchen/conversion-factor")}
                                    className="px-3 py-1.5 rounded-md font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 hover:shadow-md text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Unit Cost
                                </button>

                                <button
                                    onClick={() => navigate("/central-kitchen/inventory-calculator")}
                                    className="px-3 py-1.5 rounded-md font-medium bg-pink-100 text-pink-700 hover:bg-pink-200 hover:shadow-md text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Inventory Calc
                                </button>
                            </div>

                            <div className="flex-1 flex justify-center">
                                <button
                                    onClick={fetchProductions}
                                    disabled={loading}
                                    className="px-4 py-2 flex gap-1.5 items-center rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FaSearch size={12} />

                                    {loading
                                        ? "Loading..."
                                        : "Search"}
                                </button>
                            </div>

                            <div className="flex-1 flex justify-end">
                                <label className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                                    <input
                                        type="checkbox"
                                        checked={showItemEntries}
                                        onChange={(e) =>
                                            setShowItemEntries(e.target.checked)
                                        }
                                        className="cursor-pointer"
                                    />

                                    View Item Entries
                                </label>
                            </div>
                        </div>
                    </div>

                    <PageStatus
                        loading={loading}
                        error={error}
                        loadingText="Loading production data..."
                    />

                    {!loading && !error && (
                        <>
                            <div className="flex justify-between items-center mb-2">
                                <h1 className="text-lg font-semibold text-gray-800">
                                    Central Kitchen Production History &nbsp; --- &nbsp;
                                    <span className="text-sm text-gray-500">
                                        View posted production entries
                                    </span>
                                </h1>

                                <button
                                    onClick={exportToExcel}
                                    disabled={
                                        !productions.length && !flatItems.length
                                    }
                                    className="px-4 py-1.5 items-center rounded-md font-medium bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Export Excel
                                </button>
                            </div>

                            <ProductionHistoryTable
                                showItemEntries={showItemEntries}
                                productions={productions}
                                flatItems={flatItems}
                                loading={loading}
                                totals={totals}
                                setSelectedProduction={setSelectedProduction}
                                formatSalesValue={formatSalesValue}
                                baseUrl={baseUrl}
                                fetchProductions={fetchProductions}
                                summaryRows={summaryRows}
                            />
                        </>
                    )}
                </div>

                {selectedProduction && (
                    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
                        <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-4">
                            <div className="flex justify-between items-center mb-2">
                                <div className="font-semibold text-gray-800">
                                    Production Items
                                </div>

                                <button
                                    onClick={() =>
                                        setSelectedProduction(null)
                                    }
                                    className="text-gray-500 hover:text-gray-700 text-2xl"
                                >
                                    ×
                                </button>
                            </div>

                            <div className="overflow-x-auto tiny-scrollbar rounded-md overflow-hidden border">
                                <table className="w-full text-sm text-center">
                                    <thead className="bg-gray-100 text-gray-700">
                                        <tr>
                                            <th className="px-3 py-2.5 font-semibold">
                                                SN
                                            </th>

                                            <th className="px-3 py-2.5 font-semibold">
                                                Item
                                            </th>

                                            <th className="px-3 py-2.5 font-semibold">
                                                Qty
                                            </th>

                                            <th className="px-3 py-2.5 font-semibold">
                                                UOM
                                            </th>

                                            <th className="px-3 py-2.5 font-semibold">
                                                Rate
                                            </th>

                                            <th className="px-3 py-2.5 font-semibold">
                                                Total
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {selectedProduction.items?.map(
                                            (item, index) => (
                                                <tr
                                                    key={item.id}
                                                    className="border-t border-gray-100"
                                                >
                                                    <td className="px-3 py-2.5">
                                                        {index + 1}
                                                    </td>

                                                    <td className="px-3 py-2.5">
                                                        {item.itemname}
                                                    </td>

                                                    <td className="px-3 py-2.5">
                                                        {parseFloat(
                                                            item.quantity
                                                        ).toFixed(2)}
                                                    </td>

                                                    <td className="px-3 py-2.5">
                                                        {item.uom}
                                                    </td>

                                                    <td className="px-3 py-2.5">
                                                        {formatSalesValue(
                                                            item.rate
                                                        )}
                                                    </td>

                                                    <td className="px-3 py-2.5 font-semibold">
                                                        {formatSalesValue(
                                                            item.total
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default CentralKitchenProductionHistory;